import { getString } from "@/common/uistringlabels/ui-string-utils";
import type { CampaignSummary } from "@amzn/hardware-self-service-api-model";
import { RESPONSE_OPTION_CONFIG } from "./response-options";
import type { CampaignResponseValue } from "./response-options";

export type { CampaignResponseValue };

export type CampaignUrgency = "overdue" | "escalating-soon" | "due-soon" | null;

export const DEFAULT_EVIDENCE_LABEL = getString("campaigns.defaultEvidenceLabel");

export interface CampaignResponseOption {
  value: CampaignResponseValue;
  label: string;
  description: string;
  requiresEvidence?: boolean;
  evidenceLabel?: string;
}

export const RESPONSE_OPTIONS: CampaignResponseOption[] = RESPONSE_OPTION_CONFIG.map((option) => {
  const config: {
    value: CampaignResponseValue;
    labelKey: string;
    descriptionKey: string;
    requiresEvidence?: boolean;
    evidenceLabelKey?: string;
  } = option;
  return {
    value: config.value,
    label: getString(`campaigns.responseOptions.${config.labelKey}`),
    description: getString(`campaigns.responseOptions.${config.descriptionKey}`),
    requiresEvidence: config.requiresEvidence,
    evidenceLabel: config.evidenceLabelKey
      ? getString(`campaigns.responseOptions.${config.evidenceLabelKey}`)
      : undefined,
  };
});

export function getResponseOption(
  value: CampaignResponseValue
): CampaignResponseOption | undefined {
  return RESPONSE_OPTIONS.find((option) => option.value === value);
}

export function responseRequiresEvidence(value: CampaignResponseValue): boolean {
  return getResponseOption(value)?.requiresEvidence ?? false;
}

const DEFAULT_ESCALATION_DAYS = 5;
const DUE_SOON_DAYS = 7;
const MILLISECONDS_PER_DAY = 1000 * 60 * 60 * 24;

export function getCampaignUrgency(campaign: CampaignSummary): CampaignUrgency {
  if (!campaign.endDate) {
    return null;
  }

  const endTime = new Date(campaign.endDate).getTime();
  if (Number.isNaN(endTime)) {
    return null;
  }

  const now = Date.now();
  // Compare raw timestamps for overdue: rounding days first would map an
  // end date less than 24h in the past to 0, masking it as escalating-soon.
  if (endTime < now) {
    return "overdue";
  }

  const daysUntilEnd = Math.ceil((endTime - now) / MILLISECONDS_PER_DAY);

  if (daysUntilEnd <= (campaign.escalationDays ?? DEFAULT_ESCALATION_DAYS)) {
    return "escalating-soon";
  }
  if (daysUntilEnd <= DUE_SOON_DAYS) {
    return "due-soon";
  }
  return null;
}
